import React, { useState, useEffect } from 'react';
import { ContentService } from '../../services/contentService';
import { SolutionService } from '../../services/solutionService';

import ImageWithFallback from './ImageWithFallback';
import SkeletonLoader from './SkeletonLoader';

interface FeaturedItem {
  id: string;
  title: string;
  excerpt: string;
  image: string;
  type: 'gtm' | 'solution' | 'blog' | 'success-story';
  href: string;
  publishDate?: Date;
  readTime?: number;
  tags?: string[];
}

interface FeaturedContentGridProps {
  maxItems?: number;
}

const FeaturedContentGrid: React.FC<FeaturedContentGridProps> = ({ maxItems = 4 }) => {
  const [featuredItems, setFeaturedItems] = useState<FeaturedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchFeaturedContent = async () => {
      try {
        setLoading(true);
        
        const targetSolutionIds = [
          '83a64d28-7f76-40ec-8190-01fccf454bbe',
          '45cb7fac-7b52-4fd1-bfcc-37ddd4a9731a'
        ];

        const targetBlogUrls = [
          'https://aws.amazon.com/blogs/modernizing-with-aws/compliant-and-cost-effective-microsoft-dynamics-365-ce-implementation-on-aws/',
          'https://aws.amazon.com/blogs/machine-learning/hcltechs-aws-powered-autowise-companion-a-seamless-experience-for-informed-automotive-buyer-decisions-with-data-driven-design/'
        ];

        // Fetch specific content
        const [solutions, allContent] = await Promise.all([
          SolutionService.getSolutionsByIds(targetSolutionIds),
          ContentService.getAllContent()
        ]);

        const items: FeaturedItem[] = [];

        // Add specific solutions in the requested order
        const orderedSolutions = targetSolutionIds
          .map(id => solutions.find(s => s.id === id))
          .filter((s): s is typeof solutions[0] => s !== undefined);

        orderedSolutions.forEach(solution => {
          items.push({
            id: solution.id,
            title: solution.title,
            excerpt: solution.summary,
            image: solution.demoVideo?.url || '/api/placeholder/400/300',
            type: 'solution',
            href: `/solutions/${solution.id}`
          });
        });

        // Add specific blogs in the requested order
        const matchingBlogs = allContent.filter(item => 
          'link' in item && item.link && targetBlogUrls.includes(item.link)
        );

        const orderedBlogs = targetBlogUrls
          .map(url => matchingBlogs.find(b => 'link' in b && b.link === url))
          .filter((b): b is typeof matchingBlogs[0] => b !== undefined);

        orderedBlogs.forEach(item => {
          const isBlog = 'author' in item;
          items.push({
            id: item.id,
            title: item.title,
            excerpt: item.excerpt,
            image: item.featuredImage.url,
            type: isBlog ? 'blog' : 'success-story',
            href: (isBlog && 'link' in item && item.link) ? item.link : `/${isBlog ? 'blog' : 'success-story'}/${item.id}`,
            publishDate: item.publishDate,
            readTime: item.readTime,
            tags: item.tags
          });
        });

        setFeaturedItems(items);
        setError(null);
      } catch (err) {
        setError('Failed to load featured content');
        console.error('Error fetching featured content:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedContent();
  }, [maxItems]);

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'gtm': return 'GTM Initiative';
      case 'solution': return 'Solution';
      case 'blog': return 'Blog Post';
      case 'success-story': return 'Success Story';
      default: return 'Content';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'gtm': return 'bg-purple-600';
      case 'solution': return 'bg-green-600';
      case 'blog': return 'bg-blue-600';
      case 'success-story': return 'bg-orange-600';
      default: return 'bg-gray-600';
    }
  };

  const formatDate = (date?: Date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
        <SkeletonLoader variant="card" count={maxItems} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="text-blue-600 hover:text-blue-800 font-medium"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
      {featuredItems.map((item) => (
        <div
          key={item.id}
          className="group cursor-pointer bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden touch-manipulation"
          onClick={() => {
            if (item.type === 'solution') {
              window.location.href = item.href;
            } else {
              window.open(item.href, '_blank', 'noopener,noreferrer');
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              if (item.type === 'solution') {
                window.location.href = item.href;
              } else {
                window.open(item.href, '_blank', 'noopener,noreferrer');
              }
            }
          }}
          role="button"
          tabIndex={0}
        >
          <div className="relative">
            <ImageWithFallback
              src={item.image}
              alt={item.title}
              className="w-full h-40 sm:h-48 object-cover group-hover:scale-105 transition-transform duration-300"
            />
            <div className="absolute top-3 sm:top-4 left-3 sm:left-4">
              <span className={`${getTypeColor(item.type)} text-white px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium`}>
                {getTypeLabel(item.type)}
              </span>
            </div>
          </div>
          
          <div className="p-4 sm:p-6">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors duration-200 line-clamp-2">
              {item.title}
            </h3>
            
            <p className="text-gray-600 mb-4 line-clamp-3 text-sm sm:text-base">
              {item.excerpt}
            </p>
            
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
              <div className="flex items-center space-x-2 sm:space-x-4 text-sm text-gray-500">
                {item.publishDate && (
                  <>
                    <span>{formatDate(item.publishDate)}</span>
                    {item.readTime && (
                      <>
                        <span>•</span>
                        <span>{item.readTime} min read</span>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
            
            {item.tags && item.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-100">
                {item.tags.slice(0, 3).map((tag) => (
                  <span
                    key={tag}
                    className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs"
                  >
                    {tag}
                  </span>
                ))}
                {item.tags.length > 3 && (
                  <span className="text-gray-500 text-xs">
                    +{item.tags.length - 3} more
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default FeaturedContentGrid;